// Crystal Thread Lattice Logo - The chosen SoulThread logo
export function LogoIcon({ size = 80 }: { size?: number }) {
  const isSmallMatrixHidden = size < 56;
  const isTiny = size < 32;
  const mainStrokeWidth = size < 64 ? 2.8 : 2.5;
  return (
    <svg
      className="logo-icon"
      width={size}
      height={size}
      viewBox="0 0 80 80"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="crystal-thread" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#67e8f9" />
          <stop offset="33%" stopColor="#a78bfa" />
          <stop offset="66%" stopColor="#f9a8d4" />
          <stop offset="100%" stopColor="#67e8f9" />
        </linearGradient>
        <filter
          id="crystal-shine"
          filterUnits="userSpaceOnUse"
          x="-40"
          y="-40"
          width="160"
          height="160"
        >
          <feGaussianBlur stdDeviation="1.5" result="crystalBlur" />
          <feSpecularLighting
            result="specOut"
            in="crystalBlur"
            specularConstant="2"
            specularExponent="20"
            lightingColor="white"
          >
            <fePointLight x="40" y="20" z="50" />
          </feSpecularLighting>
          <feComposite in="specOut" in2="SourceAlpha" operator="in" result="specOut2" />
          <feComposite
            in="SourceGraphic"
            in2="specOut2"
            operator="arithmetic"
            k1="0"
            k2="1"
            k3="1"
            k4="0"
          />
        </filter>
        <filter
          id="shadow-blur"
          x="-40"
          y="-40"
          width="160"
          height="160"
          filterUnits="userSpaceOnUse"
        >
          <feGaussianBlur stdDeviation=".75" result="shadowBlur" />
        </filter>
        <filter
          id="shadow-blur-more"
          x="-40"
          y="-40"
          width="160"
          height="160"
          filterUnits="userSpaceOnUse"
        >
          <feGaussianBlur stdDeviation="2.5" result="shadowBlur" />
        </filter>
        <radialGradient id="orb-gradient" cx="40" cy="40" r="9" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
          <stop offset="35%" stopColor="#f9a8d4" stopOpacity="0.6" />
          <stop offset="70%" stopColor="#a78bfa" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#67e8f9" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="halo-gradient" cx="40" cy="40" r="14" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="35%" stopColor="#a78bfa" stopOpacity="0.18" />
          <stop offset="75%" stopColor="#67e8f9" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#67e8f9" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="center-mask-gradient"
          cx="40"
          cy="40"
          r="12"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0%" stopColor="black" stopOpacity="1" />
          <stop offset="20%" stopColor="black" stopOpacity="1" />
          <stop offset="50%" stopColor="white" stopOpacity="1" />
          <stop offset="100%" stopColor="white" stopOpacity="1" />
        </radialGradient>
        <mask
          id="fade-center"
          maskUnits="userSpaceOnUse"
          maskContentUnits="userSpaceOnUse"
          style={{ maskType: 'luminance' }}
        >
          <rect x="0" y="0" width="80" height="80" fill="url(#center-mask-gradient)" />
        </mask>
      </defs>
      <style>
        {`
          @keyframes crystal-refract {
            0%, 100% { transform: rotate(0deg) scale(1); opacity: 0.8; }
            50% { transform: rotate(180deg) scale(1.05); opacity: 1; }
          }
          @keyframes thread-crystal {
            0% { stroke-dashoffset: 0; }
            100% { stroke-dashoffset: -13; }
          }
          @keyframes thread-crystal-11-5 {
            0% { stroke-dashoffset: 0; }
            100% { stroke-dashoffset: -11.5; }
          }
          @keyframes matrix-flow {
            0% { stroke-dashoffset: 0; }
            100% { stroke-dashoffset: -6; }
          }
          .crystal-facet {
            animation: crystal-refract 10s ease-in-out infinite;
            transform-origin: center;
          }
          .crystal-thread {
            animation: thread-crystal 0.8s linear infinite;
          }
          .matrix-border {
            stroke-dasharray: 2 4;
            animation: matrix-flow 0.6s linear infinite;
          }
          @keyframes center-spin {
            to { transform: rotate(360deg); }
          }
          .center-spinner {
            animation: center-spin 4s linear infinite;
            transform-origin: center;
          }
          @keyframes orb-pulse {
            0%, 100% { transform: scale(0.98); opacity: 0.85; }
            50% { transform: scale(1.06); opacity: 1; }
          }
          .center-orb {
            animation: orb-pulse 2.4s ease-in-out infinite;
            transform-origin: center;
          }
          @keyframes nucleus-twinkle {
            0%, 100% { opacity: 0.85; transform: scale(1); }
            50% { opacity: 1; transform: scale(1.04); }
          }
          .center-nucleus {
            animation: nucleus-twinkle 6.5s ease-in-out infinite;
            transform-origin: center;
          }
          @media (prefers-reduced-motion: reduce) {
            .crystal-facet, .crystal-thread, .matrix-border, .center-spinner, .center-orb, .center-nucleus {
              animation: none !important;
            }
          }
        `}
      </style>

      {/* Crystal lattice background with matrix animation */}
      {!isSmallMatrixHidden && (
        <g opacity="0.3">
          <polygon
            points="40,10 60,25 60,55 40,70 20,55 20,25"
            fill="none"
            stroke="url(#crystal-thread)"
            strokeWidth="0.5"
            className="matrix-border"
          />
          <line
            x1="40"
            y1="10"
            x2="40"
            y2="70"
            stroke="url(#crystal-thread)"
            strokeWidth="0.5"
            className="matrix-border"
          />
          <line
            x1="20"
            y1="25"
            x2="60"
            y2="55"
            stroke="url(#crystal-thread)"
            strokeWidth="0.5"
            className="matrix-border"
          />
          <line
            x1="60"
            y1="25"
            x2="20"
            y2="55"
            stroke="url(#crystal-thread)"
            strokeWidth="0.5"
            className="matrix-border"
          />
        </g>
      )}

      {/* Glowing orb - sits under spinner and threads */}
      {isTiny ? (
        <g className="center-orb" opacity="0.9" pointerEvents="none">
          <circle cx="40" cy="40" r="4" fill="url(#orb-gradient)" />
        </g>
      ) : (
        <g className="center-orb" opacity="0.9" pointerEvents="none">
          <circle cx="40" cy="40" r="2.2" fill="#ffffff" opacity="0.9" filter="url(#shadow-blur)" />
          <circle cx="40" cy="40" r="6" fill="url(#orb-gradient)" filter="url(#shadow-blur)" />
          <circle
            cx="40"
            cy="40"
            r="10"
            fill="url(#halo-gradient)"
            filter="url(#shadow-blur-more)"
          />
        </g>
      )}

      {/* Center spinner to mask dash gaps dynamically */}
      {!isTiny && (
        <g
          className="center-spinner"
          opacity="0.35"
          filter="url(#shadow-blur-more)"
          style={{ animationDuration: '2.2s' }}
        >
          <rect x="37" y="39.7" width="6" height="0.6" rx="0.3" fill="#a78bfa" />
          <rect
            x="37"
            y="39.7"
            width="6"
            height="0.6"
            rx="0.3"
            fill="#f9a8d4"
            transform="rotate(90 40 40)"
          />
          <rect
            x="37"
            y="39.7"
            width="6"
            height="0.6"
            rx="0.3"
            fill="#67e8f9"
            transform="rotate(45 40 40)"
          />
          <rect
            x="37"
            y="39.7"
            width="6"
            height="0.6"
            rx="0.3"
            fill="#a78bfa"
            transform="rotate(-45 40 40)"
          />
        </g>
      )}

      {/* Crystal infinity threads */}
      <g className="crystal-facet" mask="url(#fade-center)">
        <path
          d="M40 40 C48 32, 56 32, 60 40 C64 48, 56 48, 48 40 C40 32, 32 32, 20 40 C16 48, 24 48, 32 40 C40 32, 48 28, 40 40"
          stroke="url(#crystal-thread)"
          strokeWidth={mainStrokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray="6.5 3 0.5 3"
          className="crystal-thread"
          filter="url(#crystal-shine)"
        />

        <path
          d="M40 40 C45 35, 50 35, 53 40 C56 45, 50 45, 45 40 C40 35, 35 35, 27 40 C24 45, 30 41, 35 40"
          stroke="url(#crystal-thread)"
          strokeWidth="1.8"
          fill="none"
          strokeLinecap="round"
          strokeDasharray="7 2 0.5 2"
          className="crystal-thread"
          opacity="0.7"
          style={{
            animation: 'thread-crystal-11-5 0.9s linear infinite',
            animationDelay: '-0.35s',
          }}
          filter="url(#shadow-blur)"
        />
      </g>

      {/* Crystal needles */}
      <g className="crystal-needle">
        <circle cx="40" cy="40" r="0.5" fill="#fff" />
        <circle cx="40" cy="40" r="0.7" fill="#fff" filter="url(#shadow-blur)" />
        <circle
          cx="40"
          cy="40"
          r="1.2"
          fill="#fff"
          className="center-nucleus"
          filter={isTiny ? undefined : 'url(#shadow-blur)'}
        />
      </g>
    </svg>
  );
}
